import React from "react";

const money = (value) => `$${value.toFixed(2)}`;

export default function Cart({ items, setCart, subtotal, shop, go }) {
  const shipping = items.length && subtotal < 40 ? 9 : 0;
  const totalQuantity = items.reduce((sum, item) => sum + item.qty, 0);
  const remaining = Math.max(0, 40 - subtotal);
  const progress = Math.min(100, (subtotal / 40) * 100);

  const changeQuantity = (key, amount) => {
    setCart((all) =>
      all.map((item) =>
        item.key === key
          ? { ...item, qty: Math.max(1, item.qty + amount) }
          : item,
      ),
    );
  };

  const removeItem = (key) => {
    setCart((all) => all.filter((item) => item.key !== key));
  };

  return (
    <main className="bg-[#f7f7f2] px-5 py-12 text-[#17234b] sm:py-16 lg:px-10">
      <div className="mx-auto max-w-[1280px]">
        <div className="flex flex-col gap-5 border-b border-[#17234b]/15 pb-8 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#6f61ff]">
              Shopping bag · {totalQuantity}{" "}
              {totalQuantity === 1 ? "item" : "items"}
            </p>
            <h1 className="mt-3 font-serif text-5xl leading-none tracking-[-0.04em] sm:text-6xl">
              Your paper edit.
            </h1>
          </div>
          <button
            type="button"
            onClick={() => shop("All")}
            className="w-fit border-b border-[#17234b] pb-1 text-[10px] font-bold uppercase tracking-[0.15em]"
          >
            Continue shopping
          </button>
        </div>

        {!items.length ? (
          <section className="mx-auto my-20 max-w-2xl border border-[#17234b]/15 bg-white px-8 py-20 text-center shadow-[0_18px_50px_rgba(23,35,75,0.06)]">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#17234b]/20 text-xl">
              0
            </span>
            <h2 className="mt-6 font-serif text-4xl">
              Your bag is currently empty.
            </h2>
            <p className="mx-auto mt-4 max-w-md text-sm leading-7 text-[#17234b]/60">
              Browse planners, journals and desk essentials made to bring more
              clarity and color to everyday routines.
            </p>
            <button
              type="button"
              onClick={() => shop("All")}
              className="mt-8 bg-[#17234b] px-8 py-4 text-[10px] font-bold uppercase tracking-[0.16em] text-white transition hover:bg-[#6f61ff]"
            >
              Explore the collection →
            </button>
          </section>
        ) : (
          <div className="mt-10 grid items-start gap-10 lg:grid-cols-[minmax(0,1fr)_390px] lg:gap-14">
            <section>
              <div className="mb-7 bg-[#eef0ff] px-5 py-4 sm:px-6">
                <div className="flex items-center justify-between gap-4 text-xs">
                  <p className="font-medium">
                    {remaining > 0
                      ? `${money(remaining)} away from free U.S. shipping`
                      : "Your order qualifies for free U.S. shipping"}
                  </p>
                  <span className="shrink-0 font-bold">$40</span>
                </div>
                <div className="mt-3 h-1 overflow-hidden bg-[#17234b]/10">
                  <div
                    className="h-full bg-[#6f61ff] transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <div className="border-t border-[#17234b]/15">
                {items.map((item) => (
                  <article
                    key={item.key}
                    className="grid grid-cols-[96px_minmax(0,1fr)] gap-5 border-b border-[#17234b]/15 py-7 sm:grid-cols-[132px_minmax(0,1fr)_110px] sm:gap-7"
                  >
                    <img
                      className="aspect-square w-full bg-[#eef0ff] object-cover"
                      src={item.images[0]}
                      alt={item.title}
                    />

                    <div className="min-w-0">
                      <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#6f61ff]">
                        {item.category} · {item.sku}
                      </p>
                      <h2 className="mt-2 font-serif text-2xl leading-7">
                        {item.title}
                      </h2>
                      <p className="mt-2 text-xs text-[#17234b]/50">
                        {item.size}
                      </p>

                      <div className="mt-5 flex flex-wrap items-center gap-4">
                        <div className="flex h-10 items-center border border-[#17234b]/20 bg-white">
                          <button
                            type="button"
                            aria-label={`Decrease ${item.title} quantity`}
                            onClick={() => changeQuantity(item.key, -1)}
                            className="h-full w-10 text-lg transition hover:bg-[#eef0ff]"
                          >
                            −
                          </button>
                          <span className="flex h-full min-w-10 items-center justify-center border-x border-[#17234b]/15 text-xs font-bold">
                            {item.qty}
                          </span>
                          <button
                            type="button"
                            aria-label={`Increase ${item.title} quantity`}
                            onClick={() => changeQuantity(item.key, 1)}
                            className="h-full w-10 text-lg transition hover:bg-[#eef0ff]"
                          >
                            +
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.key)}
                          className="text-[9px] font-bold uppercase tracking-[0.12em] text-[#17234b]/55 underline underline-offset-4 hover:text-[#6f61ff]"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <div className="col-start-2 flex items-baseline justify-between sm:col-start-auto sm:block sm:text-right">
                      <span className="text-[10px] uppercase tracking-[0.12em] text-[#17234b]/45 sm:hidden">
                        Item total
                      </span>
                      <b className="text-sm">{money(item.price * item.qty)}</b>
                      <p className="mt-2 hidden text-[10px] text-[#17234b]/40 sm:block">
                        {money(item.price)} each
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <aside className="sticky top-28 bg-[#17234b] p-7 text-white sm:p-8">
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#c8e84b]">
                Order overview
              </p>
              <h2 className="mt-3 font-serif text-4xl">Summary</h2>

              <div className="mt-8 space-y-4 border-y border-white/15 py-6 text-sm">
                <p className="flex justify-between gap-5">
                  <span className="text-white/60">Merchandise</span>
                  <b>{money(subtotal)}</b>
                </p>
                <p className="flex justify-between gap-5">
                  <span className="text-white/60">Shipping</span>
                  <b>{shipping ? money(shipping) : "Complimentary"}</b>
                </p>
                <p className="flex justify-between gap-5">
                  <span className="text-white/60">Taxes</span>
                  <span className="text-white/60">Calculated at checkout</span>
                </p>
              </div>

              <div className="flex items-end justify-between pt-6">
                <span className="text-xs font-bold uppercase tracking-[0.12em]">
                  Estimated total
                </span>
                <b className="font-serif text-3xl">
                  {money(subtotal + shipping)}
                </b>
              </div>

              <button
                type="button"
                onClick={() => go("checkout")}
                className="mt-8 flex w-full items-center justify-between bg-[#c8e84b] px-6 py-4 text-[10px] font-bold uppercase tracking-[0.16em] text-[#17234b] transition hover:bg-white"
              >
                <span>Continue to checkout</span>
                <span>→</span>
              </button>

              <p className="mt-5 text-center text-[10px] leading-5 text-white/45">
                Secure checkout · Final delivery dates confirmed before payment
              </p>
            </aside>
          </div>
        )}
      </div>
    </main>
  );
}
